/**
 * Coil rack geometry for Hub Cal+Test.
 *
 * Single source of truth: pick coil dimensions, place fixed-size slot cards outside
 * that coil with a constant outward offset. Never scale the coil independently of
 * slot centers (that is what put the outline under the cards).
 */

export type BenchCoilShape = "rectangle" | "diamond";

export interface BenchRackSlotPosition {
  slotName: string;
  x: number;
  y: number;
}

export interface BenchRackViewBox {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

export interface BenchRackLayout {
  coilShape: BenchCoilShape;
  positions: BenchRackSlotPosition[];
  viewBox: BenchRackViewBox;
  /** Upper-right vertical corner — lead attaches here (diamond). */
  leadAttach?: { x: number; y: number };
}

/** Fixed slot card size in SVG units (do not scale with coil). */
export const BENCH_RACK_SLOT_WIDTH = 96;
export const BENCH_RACK_SLOT_HEIGHT = 50;
export const BENCH_RACK_LEAD_WIDTH = 150;
export const BENCH_RACK_LEAD_HEIGHT = 8;

/**
 * Hub display diamond — NiceGUI segment counts, stretched so 96×50 cards clear the
 * outline and do not overlap on the short top-left diagonal. Verified by
 * ops/tmp/verify-caltest-coil-layout.py (coil-vs-card + card-vs-card).
 *
 * NiceGUI baseline is 210 / 355 / 250 / 48; that puts the 3px stroke under the cards
 * (outward == half card width). Apex is raised so four top-left cards have room.
 */
const DISPLAY_DIAMOND_HALF_WIDTH = 270;
const DISPLAY_DIAMOND_APEX_OFFSET_Y = 455;
const DISPLAY_DIAMOND_VERTICAL_CORNER_OFFSET_Y = 250;
/** Distance from coil edge to slot center; must exceed half card width + stroke. */
const DISPLAY_DIAMOND_OUTWARD_OFFSET = 54;
const DIAMOND_SEGMENT_SLOT_COUNTS = [6, 2, 1, 8, 4, 2] as const;

const RECTANGLE_COIL_WIDTH = 420;
const RECTANGLE_COIL_HEIGHT = 710;

export function diamondCoilVertices(
  halfWidth = DISPLAY_DIAMOND_HALF_WIDTH,
  apexOffsetY = DISPLAY_DIAMOND_APEX_OFFSET_Y,
  verticalCornerOffsetY = DISPLAY_DIAMOND_VERTICAL_CORNER_OFFSET_Y,
): ReadonlyArray<{ x: number; y: number }> {
  return [
    { x: 0, y: -apexOffsetY },
    { x: halfWidth, y: -verticalCornerOffsetY },
    { x: halfWidth, y: verticalCornerOffsetY },
    { x: 0, y: apexOffsetY },
    { x: -halfWidth, y: verticalCornerOffsetY },
    { x: -halfWidth, y: -verticalCornerOffsetY },
  ];
}

export function rectangleCoilBounds(): { width: number; height: number } {
  return { width: RECTANGLE_COIL_WIDTH, height: RECTANGLE_COIL_HEIGHT };
}

function interpolateSegment(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  pointCount: number,
): Array<{ x: number; y: number }> {
  if (pointCount <= 0) return [];
  return Array.from({ length: pointCount }, (_, index) => {
    const t = (index + 0.5) / pointCount;
    return {
      x: startX + (endX - startX) * t,
      y: startY + (endY - startY) * t,
    };
  });
}

function buildDiamondPositionsFromVertices(
  vertices: ReadonlyArray<{ x: number; y: number }>,
  outwardOffset: number,
): BenchRackSlotPosition[] {
  const segmentPairs = [
    [vertices[1], vertices[2]],
    [vertices[2], vertices[3]],
    [vertices[3], vertices[4]],
    [vertices[4], vertices[5]],
    [vertices[5], vertices[0]],
    [vertices[0], vertices[1]],
  ] as const;
  const ordered: Array<{ x: number; y: number }> = [];
  for (let segmentIndex = 0; segmentIndex < segmentPairs.length; segmentIndex += 1) {
    const [start, end] = segmentPairs[segmentIndex];
    const pointCount = DIAMOND_SEGMENT_SLOT_COUNTS[segmentIndex];
    const perimeterPoints = interpolateSegment(start.x, start.y, end.x, end.y, pointCount);
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const isVertical = Math.abs(dx) < 1;
    for (const point of perimeterPoints) {
      if (isVertical) {
        const segmentLength = Math.hypot(dx, dy);
        const normalX = segmentLength > 0 ? dy / segmentLength : 0;
        const normalY = segmentLength > 0 ? -dx / segmentLength : 0;
        ordered.push({
          x: point.x + normalX * outwardOffset,
          y: point.y + normalY * outwardOffset,
        });
      } else {
        const radialLength = Math.hypot(point.x, point.y);
        const radialX = radialLength > 0 ? point.x / radialLength : 0;
        const radialY = radialLength > 0 ? point.y / radialLength : 0;
        ordered.push({
          x: point.x + radialX * outwardOffset,
          y: point.y + radialY * outwardOffset,
        });
      }
    }
  }
  return ordered.map((point, index) => ({
    slotName: String(index + 1),
    x: point.x,
    y: point.y,
  }));
}

function buildRectanglePositions(): BenchRackSlotPosition[] {
  const topCount = 4;
  const bottomCount = 4;
  const leftCount = 9;
  const rightCount = 9;
  const rowCornerGap = 90;
  const columnCornerGap = 80;
  const rowSlotOffset = 27;
  const columnSlotOffset = 40;
  const rowSpan = RECTANGLE_COIL_WIDTH + 2 * rowSlotOffset - 2 * rowCornerGap;
  const rowSpacing = rowSpan / (Math.max(topCount, bottomCount) - 1);
  const topY = -RECTANGLE_COIL_HEIGHT / 2 - rowSlotOffset;
  const bottomY = RECTANGLE_COIL_HEIGHT / 2 + rowSlotOffset;
  const rowStartX = -rowSpan / 2;
  const columnSpan = RECTANGLE_COIL_HEIGHT + 2 * columnSlotOffset - 2 * columnCornerGap;
  const leftSpacing = columnSpan / (leftCount - 1);
  const rightSpacing = columnSpan / (rightCount - 1);
  const columnStartY = -columnSpan / 2;
  const leftX = -RECTANGLE_COIL_WIDTH / 2 - columnSlotOffset;
  const rightX = RECTANGLE_COIL_WIDTH / 2 + columnSlotOffset;

  const topPositions = Array.from({ length: topCount }, (_, index) => ({
    x: rowStartX + index * rowSpacing,
    y: topY,
  }));
  const rightPositions = Array.from({ length: rightCount }, (_, index) => ({
    x: rightX,
    y: columnStartY + index * rightSpacing,
  }));
  const bottomPositions = Array.from({ length: bottomCount }, (_, index) => ({
    x: rowStartX + index * rowSpacing,
    y: bottomY,
  }));
  const leftPositions = Array.from({ length: leftCount }, (_, index) => ({
    x: leftX,
    y: columnStartY + index * leftSpacing,
  }));

  const ordered = [
    ...rightPositions,
    ...bottomPositions.slice().reverse(),
    ...leftPositions.slice().reverse(),
    ...topPositions,
  ];
  return ordered.map((point, index) => ({
    slotName: String(index + 1),
    x: point.x,
    y: point.y,
  }));
}

function viewBoxForLayout(
  coilShape: BenchCoilShape,
  positions: BenchRackSlotPosition[],
  vertices: ReadonlyArray<{ x: number; y: number }> | null,
): BenchRackViewBox {
  const halfSlotWidth = BENCH_RACK_SLOT_WIDTH / 2;
  const halfSlotHeight = BENCH_RACK_SLOT_HEIGHT / 2;
  const xs: number[] = [];
  const ys: number[] = [];
  for (const position of positions) {
    xs.push(position.x - halfSlotWidth, position.x + halfSlotWidth);
    ys.push(position.y - halfSlotHeight, position.y + halfSlotHeight);
  }
  if (coilShape === "diamond" && vertices) {
    for (const vertex of vertices) {
      xs.push(vertex.x);
      ys.push(vertex.y);
    }
    xs.push(vertices[1].x + BENCH_RACK_LEAD_WIDTH);
  } else {
    const { width, height } = rectangleCoilBounds();
    xs.push(-width / 2, width / 2 + BENCH_RACK_LEAD_WIDTH);
    ys.push(-height / 2, height / 2);
  }
  const padding = 20;
  const minX = (xs.length ? Math.min(...xs) : 0) - padding;
  const maxX = (xs.length ? Math.max(...xs) : 0) + padding;
  const minY = (ys.length ? Math.min(...ys) : 0) - padding;
  const maxY = (ys.length ? Math.max(...ys) : 0) + padding;
  return {
    minX,
    minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function buildDiamondDisplayLayout(): BenchRackLayout {
  const vertices = diamondCoilVertices();
  const positions = buildDiamondPositionsFromVertices(vertices, DISPLAY_DIAMOND_OUTWARD_OFFSET);
  return {
    coilShape: "diamond",
    positions,
    viewBox: viewBoxForLayout("diamond", positions, vertices),
    leadAttach: { x: vertices[1].x, y: vertices[1].y },
  };
}

export function buildBenchRackLayout(coilShape: string | null | undefined): BenchRackLayout {
  const normalized = String(coilShape ?? "rectangle").trim().toLowerCase() === "diamond" ? "diamond" : "rectangle";
  if (normalized === "diamond") {
    return buildDiamondDisplayLayout();
  }
  const positions = buildRectanglePositions();
  const { width } = rectangleCoilBounds();
  return {
    coilShape: "rectangle",
    positions,
    viewBox: viewBoxForLayout("rectangle", positions, null),
    leadAttach: { x: width / 2, y: 0 },
  };
}

/**
 * Hub display layout. For diamond, always rebuild coil+slots together from display
 * dimensions so the outline stays inside the fixed-size cards. Engine positions are
 * ignored for placement (slot names still match for assignments).
 */
export function buildBenchRackDisplayLayout(input?: {
  coilShape?: string | null;
  positions?: Array<{ slotName: string; x: number; y: number }> | null;
} | null): BenchRackLayout {
  const coilShape =
    String(input?.coilShape ?? "diamond").trim().toLowerCase() === "rectangle" ? "rectangle" : "diamond";
  return buildBenchRackLayout(coilShape);
}

export function resolveBenchRackLayout(input?: {
  coilShape?: string | null;
  positions?: Array<{ slotName: string; x: number; y: number }> | null;
  viewBox?: BenchRackViewBox | null;
} | null): BenchRackLayout {
  return buildBenchRackDisplayLayout(input);
}
